import DownloadIcon from "@mui/icons-material/Download";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { DocumentFileType } from "documents/document-file-type";
import { filesize } from "filesize";
import { useGetMessageImagesQuery } from "messaging/_api/messaging-api";
import AttachmentProcessingState from "messaging/attachments/_views/attachment-processing-state";
import { FileScanStatus } from "messaging/attachments/file-info/file-scan-status";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { HOVER_ACTION_CLASS, revealHoverActions } from "messaging/correspondence/_views/message-hover-actions";
import { unreadRule } from "messaging/correspondence/_views/read-state-controls";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useEffect } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";

/**
 * The chat bubble both Correspondence panes draw a message in: the reader's own on the right in the dark
 * bubble, everybody else's on the left in the light one, with the avatar, name and time in the info row
 * and the message's controls beside them.
 *
 * The work agreement's chat drew these; the proposal's comments were rows in a gray list. Both draw
 * this now, so a message looks the same in either pane.
 */
export type BubbleSide = "sent" | "received";

const Container = styled("div", {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: BubbleSide }>(({ theme, side }) => ({
  "@container (min-width: 0px)": {
    maxWidth: "90%",
  },
  "@container (min-width: 600px)": {
    maxWidth: "75%",
  },
  "@container (min-width: 800px)": {
    maxWidth: "50%",
  },
  "@container (min-width: 1000px)": {
    maxWidth: "40%",
  },
  // A received message's avatar stands beside the name and the bubble, not over them.
  alignItems: "flex-start",
  columnGap: theme.spacing(1),
  display: side === "received" ? "flex" : "block",
  margin: side === "sent" ? theme.spacing(1.5, 0, 1.5, "auto") : theme.spacing(1.5, "auto", 1.5, 0),
  width: "fit-content",
  ...revealHoverActions,
}));

/** A received message's name row and bubble, beside its avatar. */
const ReceivedColumn = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));

/**
 * The row over a received message: the name, then the time and the status small and gray on the same
 * line, then the controls. Under the name, the time doubled the row's height for one short word.
 *
 * The name gives way first: it takes up to two lines, then ends in an ellipsis. The time beside it is
 * the pane's short form in a narrow pane ("5m", "Oct 7"), so it leaves the name most of the line. The
 * controls never wrap onto a line of their own (#5839).
 */
const ReceivedInfo = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "flex",
  marginBottom: theme.spacing(0.25),
  maxWidth: "100%",
  minHeight: RECEIVED_AVATAR_SIZE,
  "& > *": {
    flexShrink: 0,
  },
  // The button is 28px tall; it does not make the row taller than the avatar beside it.
  [`& > .${CONTROLS_CLASS}`]: { marginBlock: "-4px" },
}));

/**
 * The row under a sent message: its controls, its status and its time, at the bubble's right edge. The
 * bubble's side and color already say whose it is, so it carries no "You" and no avatar. It may wrap, as
 * a last resort: a published comment's three controls need more room than a 287px proposal pane gives.
 */
const SentInfo = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  marginTop: theme.spacing(0.25),
  rowGap: theme.spacing(0.5),
  "& > *": {
    flexShrink: 0,
  },
}));

/** The status and the time, never narrower than the time, which does not wrap. */
const Details = styled("span")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "flex",
  minWidth: "min-content",
  "& > *": {
    flexShrink: 0,
  },
}));

const Status = styled("span")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "inline-flex",
  "&:empty": { display: "none" },
}));

/**
 * The name, on as many as two lines before it ends in an ellipsis. Whoever passes it gives it a
 * `title`, so the whole of it shows under the pointer.
 */
const Sender = styled("span")(() => ({
  display: "flex",
  minWidth: 0,
  "&&": {
    flexShrink: 1,
  },
  // Doubled, to win over the `nowrap` the hosts give their name elements.
  "&& > *": {
    display: "-webkit-box",
    fontSize: "0.8125rem",
    fontWeight: 500,
    lineHeight: 1.4,
    minWidth: 0,
    overflow: "hidden",
    overflowWrap: "anywhere",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    whiteSpace: "normal",
  },
}));

const Controls = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
}));

/**
 * The time a message was sent, small and gray, on one line: an edited message's time used to wrap
 * under its "Edited".
 */
export const BubbleTime = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.4,
  whiteSpace: "nowrap",
}));

const AVATAR_CLASS = "MessageBubble-avatar";
const CONTROLS_CLASS = "MessageBubble-controls";
const RECEIVED_AVATAR_SIZE = "28px";
const DETAILS_CLASS = "MessageBubble-details";
const SENDER_CLASS = "MessageBubble-sender";
const STATUS_CLASS = "MessageBubble-status";
const TIME_CLASS = "MessageBubble-time";

const SenderAvatar = styled(Avatar)(() => ({
  height: RECEIVED_AVATAR_SIZE,
  width: RECEIVED_AVATAR_SIZE,
}));

/** Holds the avatar; a host's own (the proposal pane's opens a profile) is held to the same size. */
const AvatarSlot = styled("span")(() => ({
  display: "flex",
  flexShrink: 0,
  "& .MuiAvatar-root": {
    height: RECEIVED_AVATAR_SIZE,
    width: RECEIVED_AVATAR_SIZE,
  },
}));

const Content = styled("div", {
  shouldForwardProp: (prop) => prop !== "side" && prop !== "unread" && prop !== "dimmed",
})<{ side: BubbleSide; unread: boolean; dimmed: boolean }>(({ theme, side, unread, dimmed }) => ({
  backgroundColor: side === "sent" ? "#3f51b5" : "#d6defd",
  borderRadius: theme.spacing(2),
  // The corner nearest its sender, cut short: a tail, without drawing one.
  ...(side === "sent" ? { borderBottomRightRadius: theme.spacing(0.5) } : { borderTopLeftRadius: theme.spacing(0.5) }),
  // The rule down an unread message's edge (#5571), on the bubble.
  borderLeft: side === "received" ? unreadRule(theme, unread) : undefined,
  color: side === "sent" ? theme.palette.common.white : undefined,
  cursor: unread ? "pointer" : undefined,
  display: "block",
  fontFamily: "Roboto, sans-serif",
  // 14px: at the page's 16px a short question took four lines of a 365px pane, and the text outweighed
  // the 13px name over it. A literal, because the theme's body2 is scaled down inside these panes.
  fontSize: "0.875rem",
  lineHeight: "20px",
  opacity: dimmed ? 0.66 : 1,
  overflowWrap: "anywhere",
  // Snug around the text, as a chat bubble is. At 24px a one-line message stood in a bubble four lines
  // tall, and every list it was in ran a third as long as it needed to.
  padding: theme.spacing(1, 1.5),
  whiteSpace: "normal",
  // A message's markdown arrives in paragraphs, nested in whatever wraps it, and a paragraph's own
  // margins added a blank line above and below the text inside the bubble's padding.
  "& p": {
    margin: 0,
  },
  "& p + p": {
    marginTop: theme.spacing(1),
  },
  "& a": {
    color: side === "sent" ? theme.palette.common.white : theme.palette.common.black,
  },
  "& blockquote": {
    borderLeft: "4px solid #ccc",
    color: "#ccc",
    margin: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  "& ul,ol": {
    listStylePosition: "inside",
    margin: 0,
    padding: 0,
  },
}));

export const BubbleSubjectChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

/** A message that is gone: italic and muted, where the message stood. */
/**
 * What a live message with no content and no attachments shows in place of its body (#6592). Its file is
 * the only thing such a message ever carried, so the reader is told that it is gone.
 */
export const NO_BODY_TEXT = "Attachment no longer available";

export const BubbleWithheldText = styled("span")({
  display: "block",
  fontStyle: "italic",
  opacity: 0.75,
});

type MessageBubbleProps = {
  side: BubbleSide;
  /** The avatar: an image source, or a node of the host's own (the proposal pane opens a profile). */
  avatar?: React.ReactNode;
  avatarSrc?: string;
  /** Who wrote it. Drawn over a received message only, as the avatar is: a sent one's side says whose it is. */
  senderName?: React.ReactNode;
  time: React.ReactNode;
  /** Edited, History, Pending: what has happened to the message. */
  status?: React.ReactNode;
  /** The message's controls, in the same row. */
  controls?: React.ReactNode;
  /** The reader has not read it: its bubble carries the unread rule, and a press marks it read. */
  unread?: boolean;
  onMarkRead?: () => void;
  /** Not delivered yet: the bubble dims. */
  dimmed?: boolean;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(function MessageBubble(props, ref) {
  const { side, avatar, avatarSrc, senderName, time, status, controls, unread, onMarkRead, dimmed, children, ...rest } =
    props;
  const isUnread = Boolean(unread);
  const pressable =
    isUnread && onMarkRead
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": "Unread message. Press to mark it read.",
          onClick: onMarkRead,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            onMarkRead();
          },
        }
      : {};

  const details = (
    <Details className={DETAILS_CLASS} data-testid="message-bubble-details">
      <Status className={STATUS_CLASS}>{status}</Status>
      <BubbleTime className={TIME_CLASS}>{time}</BubbleTime>
    </Details>
  );

  const content = (
    <Content
      side={side}
      unread={isUnread}
      dimmed={Boolean(dimmed)}
      data-testid="message-bubble-content"
      data-unread={isUnread ? "true" : undefined}
      {...pressable}
    >
      {children}
    </Content>
  );

  if (side === "sent") {
    return (
      <Container ref={ref} side={side} data-side={side} {...rest}>
        {content}
        <SentInfo data-testid="message-bubble-info">
          {controls && <Controls className={`${CONTROLS_CLASS} ${HOVER_ACTION_CLASS}`}>{controls}</Controls>}
          {details}
        </SentInfo>
      </Container>
    );
  }

  return (
    <Container ref={ref} side={side} data-side={side} {...rest}>
      <AvatarSlot className={AVATAR_CLASS}>{avatar ?? <SenderAvatar src={avatarSrc} />}</AvatarSlot>
      <ReceivedColumn>
        <ReceivedInfo data-testid="message-bubble-info">
          {senderName && (
            <Sender className={SENDER_CLASS} data-testid="message-bubble-sender">
              {senderName}
            </Sender>
          )}
          {details}
          {controls && <Controls className={CONTROLS_CLASS}>{controls}</Controls>}
        </ReceivedInfo>
        {content}
      </ReceivedColumn>
    </Container>
  );
});

// #region Files
const Attachment = styled("figure")(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(0.75),
  width: "100%",
}));
const ImagePreview = styled(Link)(() => ({
  cursor: "pointer",
  display: "block",
}));
/** A picture with the bubble's own rounding, held to a height that leaves the conversation readable. */
const ImageAttachment = styled("img")(({ theme }) => ({
  borderRadius: theme.spacing(1),
  display: "block",
  maxHeight: 240,
  objectFit: "cover",
  width: "100%",
}));
/**
 * The file's card: its type icon, its name over its size and type, and its buttons, on one row inside
 * the bubble. A tint of white on either bubble, so it reads as an object the message carries.
 *
 * It replaces a 64px type icon over a one-line name in 12px and two gray discs: the name was cut to
 * "Filing Receipt - US 18-123,456...." to leave room for the discs, and the discs, white on gray on the
 * light bubble, were the hardest thing in the pane to see.
 */
const FileCard = styled("figcaption", {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: BubbleSide }>(({ theme, side }) => ({
  alignItems: "center",
  backgroundColor: side === "sent" ? "rgba(255, 255, 255, 0.14)" : "rgba(255, 255, 255, 0.7)",
  borderRadius: theme.spacing(1),
  columnGap: theme.spacing(1),
  display: "flex",
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0.75, 0.75, 0.75, 1),
}));
const FileTypeIcon = styled(Link)(() => ({
  cursor: "pointer",
  display: "block",
  flexShrink: 0,
  lineHeight: 0,
  width: 28,
}));
const FileText = styled("span")(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minWidth: 0,
}));
/** Two lines of the name before it ends in an ellipsis: a filing's name is most of what tells it apart. */
const FileName = styled("span")(() => ({
  display: "-webkit-box",
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.3,
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
const FileMeta = styled("span")(() => ({
  fontSize: "0.75rem",
  lineHeight: 1.4,
  opacity: 0.75,
}));
/** Why a file's buttons are disabled: its malware scan has not cleared it (#6916). */
const WithheldReason = styled("span")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.5),
  display: "flex",
  fontSize: "0.75rem",
  fontStyle: "italic",
  lineHeight: 1.4,
}));
/** A plain icon in the bubble's own colors: white on the dark bubble, the primary blue on the light one. */
const FileAction = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: BubbleSide }>(({ theme, side }) => ({
  borderRadius: theme.spacing(0.75),
  color: side === "sent" ? theme.palette.common.white : theme.palette.primary.main,
  flexShrink: 0,
  padding: 4,
  "&:hover": {
    backgroundColor: side === "sent" ? "rgba(255, 255, 255, 0.16)" : alpha(theme.palette.primary.main, 0.08),
  },
  // Withheld until its scan clears (#6916): faded, and still visible on the dark bubble.
  "&.Mui-disabled": {
    color: side === "sent" ? "rgba(255, 255, 255, 0.45)" : theme.palette.action.disabled,
  },
}));
const LoadingIndicator = styled(CircularProgress, {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: BubbleSide }>(({ side }) => ({
  color: side === "sent" ? "white" : "black",
  display: "flex",
  flexShrink: 0,
  height: 28,
  width: 28,
}));

/** The part of a file's name after its last dot: "Receipt - US 18-123,456.pdf" is a pdf. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1);
}

type MessageBubbleFilesProps = {
  side: BubbleSide;
  files: readonly MessageFileInfo[];
  /** Where the files were shared, which is where their images and downloads are asked for. */
  forumId?: string;
  messageId?: string;
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  /** An image was pressed, with its data. A host with no preview downloads it instead. */
  onImageClick?: (image: MessageFileInfo) => void;
  onDownload: (file: MessageFileInfo) => void;
  /** Offers Copy to documents beside each file's download button (#6053). */
  onCopyToDocuments?: (file: MessageFileInfo) => void;
};

/**
 * A message's files: an image's picture over its card, any other file's card with its type icon. The
 * card names the file, gives its size and type, and offers Copy to documents and Download.
 *
 * A file its malware scan has not cleared opens nothing and offers no download or copy, and its card
 * says why: still being scanned, malware found, or not scannable (#6916). The server would refuse
 * either one.
 */
export function MessageBubbleFiles(props: Readonly<MessageBubbleFilesProps>) {
  const { side, files, forumId, messageId, onImageLoad, onImageClick, onDownload, onCopyToDocuments } = props;
  const hasImages = files.some((file) => file.isImage);
  const imageQueryArgs = hasImages && forumId && messageId ? { forumId, messageId } : skipToken;
  const { data, isFetching, isError } = useGetMessageImagesQuery(imageQueryArgs);
  const images = React.useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (!isError) return;
    enqueueSnackbar("Failed to get message attachment. Please try again.", { variant: "error" });
  }, [isError]);

  function open(file: MessageFileInfo) {
    if (isFetching) return;
    const image = images.find((candidate) => candidate.id.isEqualTo(file.id));
    if (file.isImage && image && onImageClick) onImageClick(image);
    else onDownload(file);
  }

  if (files.length === 0) return null;

  return (
    <>
      {files.map((file) => {
        const extension = extensionOf(file.name);
        const meta = extension ? `${filesize(file.fileSize)} · ${extension.toLocaleUpperCase()}` : filesize(file.fileSize);
        const showsPicture = file.isImage && !isFetching;
        const withheldReason = file.withheldReason;
        const openFile = withheldReason ? undefined : () => open(file);
        return (
          <Attachment key={file.id.value}>
            {showsPicture && (
              <ImagePreview data-testid="message-file-preview" onClick={openFile}>
                <ImageAttachment
                  alt={file.name}
                  src={images.find((image) => image.id.isEqualTo(file.id))?.dataUri}
                  onLoad={onImageLoad}
                />
              </ImagePreview>
            )}
            <FileCard side={side} data-testid="message-file-card">
              {isFetching && <LoadingIndicator side={side} disableShrink thickness={3} />}
              {!isFetching && !file.isImage && (
                <FileTypeIcon data-testid="message-file-preview" onClick={openFile}>
                  <FileIcon
                    extension={enumKeyOf(DocumentFileType, extension).toLocaleLowerCase()}
                    {...defaultStyles[extension.toLocaleLowerCase() as keyof typeof defaultStyles]}
                  />
                </FileTypeIcon>
              )}
              <FileText>
                <FileName title={file.name}>{file.name}</FileName>
                <FileMeta>{meta}</FileMeta>
                {withheldReason && (
                  <WithheldReason role="status">
                    {file.scanStatus === FileScanStatus.Pending && (
                      <CircularProgress size={12} color="inherit" aria-hidden />
                    )}
                    {withheldReason}
                  </WithheldReason>
                )}
                {/* How far the file's processing got, then its subject matters (#6877). */}
                <AttachmentProcessingState file={file} />
              </FileText>
              {onCopyToDocuments && (
                <Tooltip title={withheldReason ?? "Copy to documents"}>
                  {/* The span keeps the button's own name: a tooltip's title would replace it. */}
                  <span>
                    <FileAction
                      side={side}
                      aria-label={`Copy ${file.name} to documents`}
                      disabled={file.isWithheld}
                      onClick={() => onCopyToDocuments(file)}
                    >
                      <DriveFileMoveOutlinedIcon fontSize="small" />
                    </FileAction>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={withheldReason ?? "Download"}>
                <span>
                  <FileAction side={side} aria-label="download" disabled={file.isWithheld} onClick={() => onDownload(file)}>
                    <DownloadIcon fontSize="small" />
                  </FileAction>
                </span>
              </Tooltip>
            </FileCard>
          </Attachment>
        );
      })}
    </>
  );
}
// #endregion
